import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface DebugFlags {
  showFormData: boolean;
  showApiCalls: boolean;
  showReservationData: boolean;
}

interface DebugContextType {
  flags: DebugFlags;
  toggleFlag: (flag: keyof DebugFlags) => void;
  setFlag: (flag: keyof DebugFlags, value: boolean) => void;
}

const DebugContext = createContext<DebugContextType | undefined>(undefined);

const DEFAULT_FLAGS: DebugFlags = {
  showFormData: false,
  showApiCalls: false,
  showReservationData: false,
};

const STORAGE_KEY = 'guilleumes_debug_flags';

export function DebugProvider({ children }: { children: ReactNode }) {
  const [flags, setFlags] = useState<DebugFlags>(() => {
    // Load from localStorage
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        return { ...DEFAULT_FLAGS, ...JSON.parse(stored) };
      } catch {
        return DEFAULT_FLAGS;
      }
    }
    return DEFAULT_FLAGS;
  });

  // Save to localStorage whenever flags change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(flags));
  }, [flags]);

  const toggleFlag = (flag: keyof DebugFlags) => {
    setFlags(prev => ({ ...prev, [flag]: !prev[flag] }));
  };

  const setFlag = (flag: keyof DebugFlags, value: boolean) => {
    setFlags(prev => ({ ...prev, [flag]: value }));
  };

  return (
    <DebugContext.Provider value={{ flags, toggleFlag, setFlag }}>
      {children}
    </DebugContext.Provider>
  );
}

export function useDebug() {
  const context = useContext(DebugContext);
  if (!context) {
    throw new Error('useDebug must be used within DebugProvider');
  }
  return context;
}
